/**
 * Resolve a *natively-registered* Capacitor plugin from the WebView bridge.
 *
 * We ship a handful of hand-written Java/Swift plugins (FileSaver,
 * PlayIntegrityPlugin, VoiceBeep, AppAttestPlugin) that are declared with
 * `@CapacitorPlugin(name = "…")` and wired in `MainActivity.registerPlugin()`.
 * They have NO JS-side `registerPlugin()` wrapper, so the only handle to them
 * is whatever the native bridge injects into the WebView.
 *
 * CAMP-2026-0249 (and the whole CAMP-2026-0221 / 0232 export family) —
 * every call site read the plugin off a bare `window[name]` global, which
 * does not exist. `JSExport.getPluginJS()` in @capacitor/android generates:
 *
 *     (function(w) {
 *       var a = (w.Capacitor = w.Capacitor || {});
 *       var p = (a.Plugins = a.Plugins || {});
 *       var t = (p['FileSaver'] = {});
 *       t.save = function(options) { … }
 *     })(window);
 *
 * i.e. the plugin lands on `window.Capacitor.Plugins[name]` and NOWHERE else.
 * Reading `window.FileSaver` therefore always returned `undefined` on device,
 * so `downloadBlob()` skipped the working native path and silently fell back
 * to an `<a download>` anchor — which the Capacitor WebView ignores. That is
 * why "Print"/"Export" looked like dead buttons in the app no matter how many
 * times the feature-level bug was "fixed".
 *
 * Resolve through the bridge first, then fall back to the legacy bare global
 * so an older shell (or a test stub) still works.
 */
export function getNativePlugin<T>(name: string): T | undefined {
  if (typeof window === "undefined") return undefined;

  const w = window as unknown as {
    Capacitor?: { Plugins?: Record<string, unknown> };
  } & Record<string, unknown>;

  const fromBridge = w.Capacitor?.Plugins?.[name];
  if (fromBridge && typeof fromBridge === "object") return fromBridge as T;

  const legacyGlobal = w[name];
  if (legacyGlobal && typeof legacyGlobal === "object") return legacyGlobal as T;

  return undefined;
}
